import React, { useEffect, useState } from "react";
import Header from "../../components/header";
import StepContent from "../../components/step-content";
import { StructuredListSkeleton } from "carbon-components-react";
import { useDispatch, useSelector } from "react-redux";
import { EnrollmentType } from "../../components/enums/enrollment-type";
import useMsalApi from "../../components/use-msal-api";
import { format, parseISO } from "date-fns";
import Employee from "./employee";
import DependentsReview from "./dependents-review";
import HealthReview from "./health-review";
import { PlanType } from "../../components/enums/plan-type";
import VoluntaryLifeReview from "./voluntary-life-review";
import FsaReview from "./fsa-review";
import SignatureReview from "./signature-review";
import { OptionalString, RequiredBoolean } from "../../components/form_elements/form-validators";
import * as Yup from "yup";
import DisabilityReview from "./disability-review";
import { formatCurrency, formatDate } from "../../components/formatters";
import BasicLifeReview from "./basic-life-review";
import BeneficiariesReview from "./beneficiaries-review";
import { useNavigate } from "react-router";
import { setAdminSessions } from "../../redux/actions/admin-actions";
import { updateEnrollments } from "../../redux/actions/enrollments-actions";
import { buildBeneficiaries, buildDependents } from "../../components/contact-builder";
import AttachmentReview from "./attachment-review";
import ErrorSummary from "../../components/form_elements/error-summary";


const Review = ({ pageStep }) => {
  const enrollments = useSelector((s) => s.enrollments);
  const admin = useSelector((s) => s.admin);
  const [unmodified, setUnmodified] = useState();
  const [changeType, setChangeType] = useState();
  const [lifeEvents, setLifeEvents] = useState();
  const [callApi] = useMsalApi();
  const [totalDeductions,setTotalDeductions] = useState(0);
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [modifiedBeneficiaries, setModifiedBeneficiaries] = useState([]);
  const [total, setTotal] = useState(0);

  const addDeduction = (val) => {
    setTotalDeductions((count) => count + val);
  };

  useEffect(() => {
    if (Array.isArray(enrollments)) return;

    setModifiedBeneficiaries(buildBeneficiaries(enrollments));
    getLifeEvents();
    setChangeType(getChangeType());
    if(!unmodified)
      getUnmodified();
  }, [enrollments]);



const getLifeEvents = async () => {
  var results = await callApi("lookup/AllQualifyingEvents", "GET");
  if(!results.error){
      setLifeEvents(results);
  }
}


  const getUnmodified = async () => {
    let path = `Enrollments/Unmodified/${enrollments.employee.id}/${format(
      parseISO(enrollments.effectiveDate),
      "yyyy-MM-dd"
    )}`;
    if (admin.isAdmin !== true) {
      path = `Enrollments/Unmodified/${format(
        parseISO(enrollments.effectiveDate),
        "yyyy-MM-dd"
      )}`;
    }
    const results = await callApi(path, "GET");
    if (!results.error) {
      setUnmodified(results);
    }
  };

  const getChangeType = () => {
    switch (enrollments.enrollmentTypeId) {
      case EnrollmentType.NewHire:
        return "New Hire";
      case EnrollmentType.OpenEnrollment:
        return "Open Enrollment";
      case EnrollmentType.LifeEvent:
        return "Qualifying Life Event";
      default:
        return "Unknown";
    }
  };

  const validationSchema = Yup.object().shape({
    signature: OptionalString().test(
      "signature-required",
      "Required",
      (item) => {
        if (admin.isAdmin === true) return true;
        return !!item;
      }
    ),
    filesNeeded: RequiredBoolean("One or more required attachments are missing.")
  });

  const onSubmit = async (formData) => {
    setSubmitting(true);

    var params = { signature: formData.signature };
    console.log("params", params, enrollments.id);
    await callApi(`Enrollments/${enrollments.id}/Submit`, "PATCH", params);
    setSubmitting(false);

    setTimeout(() => {
      dispatch(updateEnrollments([]));
    }, 1000);

    if (admin.isAdmin) {
      dispatch(
        setAdminSessions(admin.sessions.filter((s) => s.id !== enrollments.id))
      );
    } else {
      getBenefits();
    }
    navigate("/Completed", { replace: true });

  };

  const getBenefits = async () => {
    const results = await callApi("Enrollments", "GET");
    if (!results.error) dispatch(updateEnrollments(results));
  };

  const onError = (errors, e) => {
    console.log("Form Submission Error", errors);
  };

  return (
    <>
      <StepContent
        pageStep={pageStep}
        schema={validationSchema}
        onSubmit={onSubmit}
        onError={onError}
      >
        <Header title="Review Selections" />
        {unmodified && enrollments ? (
          <>
            <p>
              <strong>Benefit Update Type</strong>
            </p>
            <p>{changeType}</p>
            {enrollments?.lifeEventDescription ? (
              <div>
                <div className="text-md my-2">
                  Reason:{" "}
                  {lifeEvents?.find((s) => s.id === enrollments?.lifeEventId)?.name}
                </div>
                <div className="italic pl-2">
                  {enrollments?.lifeEventDescription}
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="my-4">
                  Effective Date :{" "}
                    {formatDate(enrollments?.effectiveDate)}
                  </div>
            <div className="italic text-md mt-8">
                  <strong>*User changes are indicated in Purple*</strong>
            </div>
            <div className="mb-6"></div>
            <Employee
              modified={enrollments?.employee}
              unmodified={unmodified.employee}
            />
            <DependentsReview
              modifiedArray={buildDependents(enrollments)}
              unmodifiedArray={buildDependents(unmodified)}
            />
            <HealthReview
              enrollments={enrollments}
              title="Medical"
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              planType={PlanType.Medical}
              addDeduction={addDeduction}
            />
            <HealthReview
              enrollments={enrollments}
              title="Dental"
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              planType={PlanType.Dental}
              addDeduction={addDeduction}
            />
            <HealthReview
              enrollments={enrollments}
              title="Vision"
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              planType={PlanType.Vision}
              addDeduction={addDeduction}
            />
            <DisabilityReview
              enrollments={enrollments}
              addDeduction={addDeduction}
            />
            <HealthReview
              enrollments={enrollments}
              title="Basic Life"
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              planType={PlanType.BasicLife}
              addDeduction={addDeduction}
            />

            <VoluntaryLifeReview
              enrollments={enrollments}
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              addDeduction={addDeduction}
            />
            <BeneficiariesReview
              modifiedArray={buildBeneficiaries(enrollments)}
              unmodifiedArray={buildBeneficiaries(unmodified)}
            />
            <FsaReview
              enrollments={enrollments}
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              addDeduction={addDeduction}
            />
              
            <AttachmentReview 
              modifiedDependents={buildDependents(enrollments)} 
              unmodifiedDependents={buildDependents(unmodified)} 
              modifiedSelections={enrollments.selections}
              unmodifiedSelections={unmodified.selections}
              contacts={enrollments.contacts}
            />
            <div
                className="pl-1 text-lg"
              >
                Total per paycheck deductions of all benefits combined:{" "}
                <strong>{formatCurrency(totalDeductions, 2)}</strong>{" "}
              </div>
            <hr />
            { <SignatureReview />/*enrollments.createdBy === enrollments.createdOnBehalfOf ? <SignatureReview /> : <></>*/}
            <ErrorSummary />
          </>
        ) : (
          <div className="my-8">
            <StructuredListSkeleton />
          </div>
        )}
      </StepContent>
    </>
  );
};

export default Review;
